import { isDefined, toError } from "@clipboard-health/util-ts";
import { onLoggedOut } from "@src/app/store/session";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { setPreference } from "@src/appV2/NativeAdapters/preferences.adapter";
import { ONBOARDING_USER_ERROR } from "@src/appV2/Worker/api/constants";
import { useGetWorker } from "@src/appV2/Worker/api/useGetWorker";
// eslint-disable-next-line no-restricted-imports
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
import {
  ActionType,
  LocalStorage,
  identifyAgent,
  loginZendesk,
  setEnvVariables,
  showAgentProfileFailedToLoadError,
} from "@store/session";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { populateSessionAfterLogin } from "./populateSessionAfterLogin";
import type { Store } from "../store/store.model";

export function useFetchAndInitializeWorker(isAuthenticated: boolean, isAuthorized: boolean): void {
  const dispatch = useDispatch();
  const isSignup = useSelector((store: Store) => store.session.isSignup);
  const isDebuggingEnabled = useCbhFlag(CbhFeatureFlag.WORKER_APP_INITIALIZATION_DEBUGGING, {
    defaultValue: false,
  });

  const {
    data: agent,
    error,
    isLoading,
    isFetching: isFetchingWorker,
    fetchStatus: fetchStatusWorker,
    status: statusWorker,
  } = useGetWorker(
    {
      enabled: isAuthenticated,
    },
    isDebuggingEnabled
  );

  useEffect(() => {
    if (!isDefined(error)) {
      return;
    }

    const errorMessage = toError(error).message;

    // Handle onboarding user error gracefully - don't show error modal
    if (errorMessage === ONBOARDING_USER_ERROR) {
      return;
    }

    // Handle 403 Forbidden errors for non-onboarded users
    if (errorMessage.includes("403") || errorMessage.includes("Forbidden")) {
      return;
    }

    // This controls AgentProfileErrorModal component, which is surprisingly only rendered in onboarding
    // We should add some more unique error screen for all type of views
    showAgentProfileFailedToLoadError(dispatch);
  }, [dispatch, error]);

  useEffect(() => {
    logEvent(APP_V2_APP_EVENTS.WORKER_INITIALIZATION_QUERY_DEBUGGING, {
      isFetchingWorker,
      isLoading,
      isAuthenticated,
      isAuthorized,
      fetchStatusWorker,
      workerId: agent?.userId,
      statusWorker,
    });
  }, [
    isFetchingWorker,
    isLoading,
    isAuthenticated,
    isAuthorized,
    fetchStatusWorker,
    agent,
    statusWorker,
  ]);

  useEffect(() => {
    void (async () => {
      logEvent(APP_V2_APP_EVENTS.WORKER_INITIALIZATION_START);

      const cognitoUser = await getCognitoSingleton().getCurrentUserInfo();
      // we don't wanna run this hook if agent is still fetching because it might cause race conditions of user id being set before
      // agent profile (when getUserByEmail finishes before actual agent fetch)
      if (!cognitoUser || isLoading) {
        logEvent(APP_V2_APP_EVENTS.WORKER_INITIALIZATION_SKIPPED, {
          isAgentQueryEnabled: isAuthenticated,
          isAgentQueryLoading: isLoading,
          isAuthenticated,
          isAuthorized,
          hasCognitoUser: Boolean(cognitoUser),
          email: cognitoUser?.email,
        });
        return;
      }

      try {
        if (agent) {
          logEvent(APP_V2_APP_EVENTS.GET_WORKER_SUCCESS);

          dispatch({
            type: ActionType.SHOW_FETCH_WORKER_FAILURE_ALERT,
            data: {
              isFetchAgentProfileFailureAlertEnabled: false,
            },
          });

          await setPreference(LocalStorage.AGENT, JSON.stringify(agent));

          void identifyAgent(agent);
        }

        if (!isAuthorized) {
          logEvent(
            APP_V2_APP_EVENTS.SESSION_POPULATED_AFTER_LOGIN_START_USE_FETCH_AND_INITIALIZE_WORKER
          );
          await populateSessionAfterLogin(dispatch, { isSignup });
          logEvent(
            APP_V2_APP_EVENTS.SESSION_POPULATED_AFTER_LOGIN_END_USE_FETCH_AND_INITIALIZE_WORKER
          );
        } else {
          logEvent(APP_V2_APP_EVENTS.SESSION_POPULATE_AFTER_LOGIN_SKIPPED);
        }

        await Promise.allSettled([loginZendesk(), setEnvVariables(dispatch)]);
      } catch (error) {
        logError(APP_V2_APP_EVENTS.WORKER_INITIALIZATION_FAILURE, {
          error,
          metadata: {
            cognitoUid: cognitoUser.uid,
            phoneNumber: cognitoUser.phoneNumber,
            email: cognitoUser.email,
            name: cognitoUser.displayName,
          },
        });

        // Dispatch a login failure action to clear session and allow for login.
        // Requiring a new login is better than hanging and requiring a force restart.
        if (isDebuggingEnabled) {
          onLoggedOut(dispatch);
        }
      }
    })();
  }, [dispatch, agent, isAuthorized, isAuthenticated, isLoading, isDebuggingEnabled, isSignup]);
}
